import { BaseQueryParams } from '../../Global.model';
import { twApi } from '../api';
import { CreateDriverData, CreateDriverParams, Driver, SearchDriverParams } from './driver.model';

const driverApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    searchDrivers: builder.query<{ content: Driver[]; total: number }, SearchDriverParams>({
      query: (searchDriverParam) => {
        const { filters, params } = searchDriverParam;

        return {
          url: 'drivers/search',
          method: 'POST',
          body: filters,
          params: params
        };
      },
      transformResponse: (response: { data: Driver[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Drivers' as const, id })),
              { type: 'Drivers', id: 'DRIVERS_LIST' }
            ]
          : [{ type: 'Drivers', id: 'DRIVERS_LIST' }]
    }),
    getDrivers: builder.query<{ content: Driver[]; total: number }, BaseQueryParams>({
      query: (arg) => ({
        url: `contacts/drivers`,
        params: { ...arg }
      }),
      transformResponse: (response: { data: Driver[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Drivers' as const, id })),
              { type: 'Drivers', id: 'DRIVERS_LIST' }
            ]
          : [{ type: 'Drivers', id: 'DRIVERS_LIST' }]
    }),
    getDriverById: builder.query<Driver, string>({
      query: (id) => {
        if (!id) throw new Error('Driver id required');
        return {
          url: `contacts/drivers/${id}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { data: Driver }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'Drivers' as const, id }]
    }),
    createDriver: builder.mutation<Driver, CreateDriverData>({
      query: (driver) => {
        const {
          firstName,
          lastName,
          companyName,
          email,
          phone,
          defaultTruckId,
          defaultTrailerId,
          defaultPayRateType,
          payRate,
          type,
          carrierId,
          createUserAndSendInvite,
          deductFuelPurchaseOnSettlement,
          eldConnectionId,
          eldData
        } = driver;

        const params: CreateDriverParams = {
          first_name: firstName,
          last_name: lastName
        };

        !!phone ? (params.phone = phone.toString()) : null;
        !!email ? (params.email_address = email) : null;
        !!companyName ? (params.company_name = companyName) : null;
        !!defaultTruckId ? (params.default_truck_id = defaultTruckId) : null;
        !!defaultTrailerId ? (params.default_trailer_id = defaultTrailerId) : null;
        !!defaultPayRateType ? (params.default_pay_rate_type = defaultPayRateType) : null;
        !!payRate ? (params.pay_rate = payRate) : null;
        !!type ? (params.type = type) : null;
        !!carrierId ? (params.carrier_id = carrierId) : null;
        !!createUserAndSendInvite
          ? (params.create_user_and_send_invite = createUserAndSendInvite)
          : null;
        !!deductFuelPurchaseOnSettlement
          ? (params.deduct_fuel_purchase_on_settlement = deductFuelPurchaseOnSettlement)
          : null;
        !!eldConnectionId ? (params.eld_connection_id = eldConnectionId) : null;
        !!eldData ? (params.eld_data = eldData) : null;

        return {
          url: 'contacts/drivers',
          method: 'POST',
          body: { ...params }
        };
      },
      transformResponse: (response: { data: Driver }) => response.data,
      invalidatesTags: [{ type: 'Drivers', id: 'DRIVERS_LIST' }]
    }),
    updateDriver: builder.mutation<Driver, { id: string; data: CreateDriverData }>({
      query: (driver) => {
        const {
          firstName,
          lastName,
          companyName,
          email,
          phone,
          defaultTruckId,
          defaultTrailerId,
          defaultPayRateType,
          payRate,
          type,
          carrierId,
          deductFuelPurchaseOnSettlement,
          eldConnectionId,
          eldData
        } = driver.data;

        const params: CreateDriverParams = {
          first_name: firstName,
          last_name: lastName,
          phone: phone || (phone === '' ? null : undefined)
        };

        !!email ? (params.email_address = email) : null;
        !!companyName ? (params.company_name = companyName) : null;
        !!defaultTruckId ? (params.default_truck_id = defaultTruckId) : null;
        !!defaultTrailerId ? (params.default_trailer_id = defaultTrailerId) : null;
        !!defaultPayRateType ? (params.default_pay_rate_type = defaultPayRateType) : null;
        !!payRate ? (params.pay_rate = payRate) : null;
        !!type ? (params.type = type) : null;
        !!carrierId ? (params.carrier_id = carrierId) : null;
        params.eld_connection_id = eldConnectionId || null;
        params.eld_data = eldData || null;
        params.deduct_fuel_purchase_on_settlement = !!deductFuelPurchaseOnSettlement;

        return {
          url: `contacts/drivers/${driver.id}`,
          method: 'PUT',
          body: { ...params }
        };
      },
      transformResponse: (response: { data: Driver }) => response.data,
      invalidatesTags: (result, error, data) => [
        { type: 'Drivers', id: data.id },
        { type: 'Drivers', id: 'DRIVERS_LIST' }
      ]
    }),
    deleteDriver: builder.mutation<Driver, string>({
      query: (id) => ({
        url: `contacts/drivers/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: (id, { dispatch, queryFulfilled }) => {
        const update = dispatch(
          driverApi.util.updateQueryData('getDrivers', {}, (driversData) => {
            const index = driversData.content.findIndex((d) => d.id === id);
            delete driversData.content[index];
            driversData.total -= 1;
          })
        );
        queryFulfilled.catch(() => {
          update.undo();
        });
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Drivers', id },
        { type: 'Drivers', id: 'DRIVERS_LIST' }
      ]
    }),
    getDriversImportTemplate: builder.mutation<void, void>({
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: 'v3/drivers/bulk-upload/template',
          method: 'GET',
          responseHandler: (response) => response.blob()
        });
        const hiddenElement = document.createElement('a');
        const url = window.URL || window.webkitURL;
        const blobCSV = url.createObjectURL(result.data as Blob);
        hiddenElement.href = blobCSV;
        hiddenElement.target = '_blank';
        hiddenElement.download = `drivers-import-template.csv`;
        hiddenElement.click();

        setTimeout(() => {
          if (document.body.contains(hiddenElement)) {
            document.body.removeChild(hiddenElement);
          }
          url.revokeObjectURL(blobCSV);
        }, 100);
        return { data: undefined };
      }
    }),
    importDriversCSV: builder.mutation<any, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        const url = 'v3/drivers/bulk-upload';
        formData.append('file', file);

        return {
          url: url,
          formData: true,
          method: 'POST',
          body: formData,
          responseHandler: (response): Promise<any> => {
            // Get the content type from the response headers
            const contentType = response.headers.get('content-type');

            // If it's CSV, return as text
            if (contentType && contentType.includes('text/csv')) {
              return response.text();
            }

            // Otherwise, return as JSON
            return response.json();
          }
        };
      },
      transformResponse: (response) => {
        // Just return the response as is - it's already been handled by responseHandler
        return response;
      }
    })
  }),
  overrideExisting: false
});

export const {
  useGetDriversQuery,
  useGetDriverByIdQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriverMutation,
  useGetDriversImportTemplateMutation,
  useImportDriversCSVMutation,
  usePrefetch: usePrefetchDriversQuery,
  useSearchDriversQuery,
  useLazySearchDriversQuery,
  util: driverApiUtil
} = driverApi;

export const toDriverDataAttributes = (driver: any): any => ({
  id: driver.id,
  first_name: driver.first_name,
  last_name: driver.last_name,
  email_address: driver.email_address,
  phone: driver.phone,
  company_name: driver.company_name,
  default_pay_rate_type: driver.default_pay_rate_type,
  pay_rate: driver.pay_rate,
  default_truck_id: driver.default_truck_id,
  default_trailer_id: driver.default_trailer_id,
  type: driver.type,
  carrier_id: driver.carrier_id,
  tenant_id: driver.tenant_id,
  twuser_id: driver.twuser_id,
  created_at: driver.createdAt,
  updated_at: driver.updatedAt,
  created_by: driver.created_by,
  updated_by: driver.updated_by,
  name: driver.name,
  initials: driver.initials,
  readable_pay_rate_type: driver.readable_pay_rate_type,
  formatted_pay_rate: driver.formatted_pay_rate
});
